import { useCallback, useMemo } from "react";

import {
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";

/**
 * =========================================================
 * usePhoneNumber
 * =========================================================
 *
 * Central phone-number handling for BluPay.
 *
 * Responsibilities:
 *
 *   - Parse numbers using libphonenumber-js
 *   - Validate numbers against a specific country
 *   - Determine the country a number belongs to
 *   - Determine whether a number is mobile
 *   - Normalize to E.164
 *   - Convert E.164 -> local/subscriber representation
 *
 * Examples:
 *
 *   GH:
 *     0241234567   -> +233241234567
 *     241234567    -> +233241234567
 *     +233241234567 -> +233241234567
 *
 *   KE:
 *     0712345678   -> +254712345678
 *     712345678    -> +254712345678
 *     +254712345678 -> +254712345678
 *
 * E.164 is the canonical format used when sending
 * phone numbers to the backend/providers.
 */

export function usePhoneNumber(countryCode) {
  const normalizedCountry = useMemo(() => {
    if (!countryCode) {
      return "";
    }

    return String(countryCode)
      .trim()
      .toUpperCase();
  }, [countryCode]);

  // =========================================================
  // PARSE
  // =========================================================

  const parse = useCallback(
    (value, overrideCountry = null) => {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        return null;
      }

      const input = String(value).trim();

      const country =
        overrideCountry
          ? String(overrideCountry)
              .trim()
              .toUpperCase()
          : normalizedCountry;

      try {
        /*
         * If the input starts with +, libphonenumber-js
         * determines the country itself.
         *
         * Otherwise, the selected corridor country is
         * used as the default country.
         */
        return parsePhoneNumberFromString(
          input,
          country || undefined
        );
      } catch {
        return null;
      }
    },
    [normalizedCountry]
  );

  // =========================================================
  // VALIDATION
  // =========================================================

  const validate = useCallback(
    (
      value,
      {
        country = normalizedCountry,
        mobileOnly = false,
      } = {}
    ) => {
      const parsed = parse(value, country);

      if (!parsed) {
        return false;
      }

      /*
       * We want the number to actually belong to the
       * selected country.
       *
       * This is important because defaultCountry is only
       * a parsing hint in libphonenumber-js.
       */
      if (
        country &&
        parsed.country &&
        parsed.country !== country
      ) {
        return false;
      }

      if (!parsed.isValid()) {
        return false;
      }

      if (mobileOnly) {
        const type = parsed.getType();

        /*
         * Some countries may not expose a type for every
         * valid number. For mobile-money corridors we want
         * an actual mobile classification.
         */
        if (type !== "MOBILE") {
          return false;
        }
      }

      return true;
    },
    [normalizedCountry, parse]
  );

  // =========================================================
  // POSSIBLE
  // =========================================================

  const isPossible = useCallback(
    (value, country = normalizedCountry) => {
      const parsed = parse(value, country);

      if (!parsed) {
        return false;
      }

      if (
        country &&
        parsed.country &&
        parsed.country !== country
      ) {
        return false;
      }

      return parsed.isPossible();
    },
    [normalizedCountry, parse]
  );

  // =========================================================
  // COUNTRY
  // =========================================================

  const getCountry = useCallback(
    (value) => {
      const parsed = parse(value);

      return parsed?.country ?? null;
    },
    [parse]
  );

  // =========================================================
  // TYPE
  // =========================================================

  const getType = useCallback(
    (value) => {
      const parsed = parse(value);

      return parsed?.getType() ?? null;
    },
    [parse]
  );

  // =========================================================
  // E.164
  // =========================================================

  const toE164 = useCallback(
    (value, country = normalizedCountry) => {
      const parsed = parse(value, country);

      if (!parsed) {
        return "";
      }

      return parsed.number;
    },
    [normalizedCountry, parse]
  );

  // =========================================================
  // NATIONAL / SUBSCRIBER NUMBER
  // =========================================================

  const toNationalNumber = useCallback(
    (value, country = normalizedCountry) => {
      const parsed = parse(value, country);

      if (!parsed) {
        return "";
      }

      /*
       * nationalNumber is exactly what we want for the
       * corridor UI:
       *
       * +233241234567
       *        ↓
       * 241234567
       *
       * +254712345678
       *        ↓
       * 712345678
       */
      return parsed.nationalNumber;
    },
    [normalizedCountry, parse]
  );

  // =========================================================
  // VALIDATION DETAILS
  // =========================================================

  const getValidation = useCallback(
    (
      value,
      {
        country = normalizedCountry,
        mobileOnly = false,
      } = {}
    ) => {
      const parsed = parse(value, country);

      if (!parsed) {
        return {
          valid: false,
          possible: false,
          country: null,
          type: null,
          e164: "",
          nationalNumber: "",
          reason: "invalid",
        };
      }

      const countryMatches =
        !country ||
        !parsed.country ||
        parsed.country === country;

      const possible =
        parsed.isPossible();

      const valid =
        countryMatches &&
        parsed.isValid();

      const type =
        parsed.getType() ?? null;

      const mobileValid =
        !mobileOnly ||
        type === "MOBILE";

      return {
        valid: valid && mobileValid,
        possible,
        country: parsed.country ?? null,
        type,
        e164: parsed.number,
        nationalNumber: parsed.nationalNumber,
        reason:
          !countryMatches
            ? "wrong_country"
            : !possible
              ? "impossible"
              : !valid
                ? "invalid"
                : mobileOnly && !mobileValid
                  ? "not_mobile"
                  : null,
      };
    },
    [normalizedCountry, parse]
  );

  return {
    countryCode: normalizedCountry,

    parse,

    validate,
    isPossible,

    getCountry,
    getType,

    toE164,
    toNationalNumber,

    getValidation,
  };
}